import React, { useState, useEffect } from 'react';
import useAuth from './hooks/useAuth';
import useInstallPrompt from './hooks/useInstallPrompt';
import { ConfirmProvider } from './contexts/ConfirmContext';
import Layout from './components/common/Layout';
import Login from './components/auth/Login';
import MyPage from './components/common/MyPage';
import Dashboard from './components/dashboard/Dashboard';
import Customer from './components/customer/Customer';
import CouponHistory from './components/coupon/CouponHistory';
import Settings from './components/settings/Settings';
import { apiFetch } from './utils/apiClient';

// 새로고침하면 항상 대시보드로 튕기던 문제 - activeTab은 원래 컴포넌트
// 로컬 state라 새로고침(리마운트) 시 초기값으로 되돌아갔다. localStorage에
// 마지막 탭을 저장해두고 로드 시 복원한다(로그인 상태를 useAuth가
// localStorage로 유지하는 것과 같은 패턴).
const VALID_TABS = ['dashboard', 'customers', 'couponHistory', 'settings'];
const ACTIVE_TAB_KEY = 'activeTab';

const AppContent = () => {
  const { isLoggedIn, storeInfo, login, logout, updateStoreInfo, checkAuthStatus } = useAuth();
  const { canInstall, promptInstall } = useInstallPrompt();
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem(ACTIVE_TAB_KEY);
    return VALID_TABS.includes(saved) ? saved : 'dashboard';
  });
  const [isMyPageOpen, setIsMyPageOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
  }, [activeTab]);

  // 페이지 로드 시 로그인 상태 복원
  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (loginData) => {
    const result = login(loginData);
    if (!result.success) {
      return result; // 에러 반환
    }
  };

  // 마이페이지 열기/닫기
  const handleMyPageOpen = () => {
    setIsMyPageOpen(true);
  };

  const handleMyPageClose = () => {
    setIsMyPageOpen(false);
  };

  // 비밀번호 변경 API 호출
  const handlePasswordUpdate = async (passwordData) => {
    try {
      const response = await apiFetch('/api/auth/update-password', {
        method: 'POST',
        body: JSON.stringify({
          storeId: storeInfo.owner_id,
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const result = await response.json();

      if (response.ok) {
        return { success: true };
      } else {
        return { success: false, error: result.message || '비밀번호 변경에 실패했습니다.' };
      }
    } catch (error) {
      console.error('비밀번호 변경 오류:', error);
      return { success: false, error: '네트워크 오류가 발생했습니다.' };
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard storeInfo={storeInfo} />;
      case 'customers':
        return <Customer storeInfo={storeInfo} />;
      case 'couponHistory':
        return <CouponHistory storeInfo={storeInfo} />;
      case 'settings':
        return <Settings storeInfo={storeInfo} onStoreInfoUpdate={updateStoreInfo} />;
      default:
        return <Dashboard storeInfo={storeInfo} />;
    }
  };

  // 로그인되지 않은 경우
  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} canInstall={canInstall} onInstallClick={promptInstall} />;
  }

  // 로그인된 경우
  return (
    <>
      <Layout
        storeInfo={storeInfo}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onLogout={logout}
        onMyPageClick={handleMyPageOpen}
        canInstall={canInstall}
        onInstallClick={promptInstall}
      >
        {renderContent()}
      </Layout>

      {/* 마이페이지 모달 */}
      {isMyPageOpen && (
        <MyPage
          storeInfo={storeInfo}
          onClose={handleMyPageClose}
          onPasswordUpdate={handlePasswordUpdate}
        />
      )}
    </>
  );
};

// ConfirmProvider가 useConfirm()을 쓰는 모든 하위 컴포넌트(Customer,
// Settings 등)를 감싸야 해서 별도 App 껍데기로 분리했다 - AppContent
// 자체는 useConfirm()을 안 쓰지만, 그 자식들이 쓸 수 있으려면 Provider가
// 트리 최상단 근처에 있어야 한다.
const App = () => (
  <ConfirmProvider>
    <AppContent />
  </ConfirmProvider>
);

export default App;